import type { ComponentRef, OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  reflectComponentType,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { ReplaySubject, switchMap } from 'rxjs';

import type { IntegrationsElement } from '@recruitee/integrations-types';

import { Destroyable, takeUntilDestroyed } from '../../common';
import { ContextualIntegrationsApiService } from '../services/contextual-integrations-api.service';
import { CurrentHotspotToken } from '../services/current-hotspot.token';
import { IntegrationKindToken } from '../services/integration-kind.token';
import type { IntegrationsElementWithComponent } from '../services/integrations-elements.service';
import { IntegrationsElementsService } from '../services/integrations-elements.service';
import { PartnerToken } from '../services/partner.token';

export function getElementSignature(element: IntegrationsElement): string {
  const idPrefix = element.id ? `${element.id}#` : '';
  return `${idPrefix}${element.integrationKind || element.partner?.codename}#${
    element.component
  }#${JSON.stringify(element.hotspot)}`;
}

@Component({
  selector: 'rti-outlet',
  template: '<ng-template #container />',
  standalone: true,
})
@Destroyable()
export class IntegrationsElementOutletComponent implements OnInit {
  @ViewChild('container', { read: ViewContainerRef, static: true })
  private container: ViewContainerRef;

  @Input() public set elements(elementsOrElement: IntegrationsElement | IntegrationsElement[]) {
    if (elementsOrElement) {
      const elements = Array.isArray(elementsOrElement) ? elementsOrElement : [elementsOrElement];
      this.hotspotElements$.next(elements);
    }
  }

  private hotspotElements$ = new ReplaySubject<IntegrationsElement[]>(1);
  private elementsTracker = new Map<string, ComponentRef<any>>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private elementsService: IntegrationsElementsService,
    private injector: Injector,
  ) {}

  public ngOnInit(): void {
    const hotspotElementsWithComponentType$: Observable<IntegrationsElementWithComponent[]> =
      this.hotspotElements$
        .asObservable()
        .pipe(switchMap(elements => this.elementsService.resolveComponents(elements)));

    hotspotElementsWithComponentType$.pipe(takeUntilDestroyed(this)).subscribe(elements => {
      this.removeExcessiveComponents(elements);
      this.createOrUpdateComponents(elements);
      this.cdRef.detectChanges();
    });
  }

  private removeExcessiveComponents(elements: IntegrationsElementWithComponent[]): void {
    const excessiveComponents = Array.from(this.elementsTracker.keys())
      .filter(
        signature => !elements.some(element => this.getElementSignature(element) === signature),
      )
      .map(excessiveSignature => ({
        signature: excessiveSignature,
        component: this.elementsTracker.get(excessiveSignature)!,
      }));

    excessiveComponents.forEach(({ signature, component }) => {
      this.container.remove(this.container.indexOf(component.hostView));
      this.elementsTracker.delete(signature);
    });
  }

  private createOrUpdateComponents(elements: IntegrationsElementWithComponent[]): void {
    elements.forEach((element, index) => {
      const signature = this.getElementSignature(element);
      const cached = this.elementsTracker.has(signature);

      const component = cached
        ? this.elementsTracker.get(signature)!
        : this.createComponent(signature, element);

      this.updateComponent(component, element);

      this.container.move(component.hostView, index);
    });
  }

  private getElementSignature(element: IntegrationsElement): string {
    return getElementSignature(element);
  }

  private createComponent(
    signature: string,
    element: IntegrationsElementWithComponent,
  ): ComponentRef<any> {
    const component = this.container.createComponent(element.componentClass, {
      index: undefined,
      injector: this.createElementInjector(element),
    });
    this.elementsTracker.set(signature, component);
    return component;
  }

  private updateComponent(
    component: ComponentRef<any>,
    element: IntegrationsElementWithComponent,
  ): void {
    const componentMirror = reflectComponentType(component.componentType)!;
    const inputKeys = componentMirror.inputs.map(input => input.propName);

    inputKeys.forEach(inputKey => {
      const value = element.inputs[inputKey];
      component.setInput(inputKey, value);
    });

    component.changeDetectorRef.detectChanges();
    component.injector.get(ChangeDetectorRef).detectChanges();
  }

  private createElementInjector(element: IntegrationsElementWithComponent): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [
        { provide: CurrentHotspotToken, useValue: element.hotspot },
        { provide: IntegrationKindToken, useValue: element.integrationKind },
        { provide: PartnerToken, useValue: element.partner },
        { provide: ContextualIntegrationsApiService, useClass: ContextualIntegrationsApiService },
      ],
    });
  }
}
